import { useEffect, useRef } from 'react';

const stats = [
  { value: '40+',    label: 'Formulations'     },
  { value: '3,750+', label: 'Global Installers' },
  { value: '75K+',   label: 'Bottles Sold'      },
  { value: '250K+',  label: 'Vehicles Coated'   },
];

const HeroSection = () => {
  const lineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const lineTimer = setTimeout(() => {
      if (lineRef.current) lineRef.current.style.height = '68%';
    }, 300);
    return () => clearTimeout(lineTimer);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative w-full overflow-hidden noise-overlay" style={{ minHeight: '100svh' }}>

      {/* ── Background ── */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/4f97c5766711858471c133a92859da26.jpg"
          alt="Advanced ceramic coating systems"
          className="w-full h-full object-cover object-center animate-hero-bg"
        />
        <div className="absolute inset-0 bg-[#030303]/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/98 via-[#030303]/85 to-[#030303]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/95 via-[#030303]/20 to-[#030303]/70" />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#c9a84c]/4" />
      </div>

      {/* ── Scan line ── */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.015]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.3) 2px, rgba(255,255,255,0.3) 3px)',
          backgroundSize: '100% 4px',
        }}
      />

      {/* ── Dot grid ── */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
        }}
      />

      {/* ── Left vertical gold accent ── */}
      <div className="absolute left-6 lg:left-14 top-0 bottom-0 flex items-center pointer-events-none" style={{ zIndex: 5 }}>
        <div
          ref={lineRef}
          className="w-[1px] bg-gradient-to-b from-transparent via-[#c9a84c]/55 to-transparent transition-all duration-[2000ms] ease-out"
          style={{ height: '0%', position: 'absolute', top: '16%' }}
        />
      </div>

      {/* ── Giant ghost wordmark ── */}
      <div className="absolute inset-0 flex items-center overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
        <span
          className="animate-hero-fade font-black leading-none select-none whitespace-nowrap"
          style={{
            fontSize: 'clamp(90px, 17vw, 240px)',
            WebkitTextStroke: '1px rgba(201,168,76,0.04)',
            color: 'transparent',
            letterSpacing: '-0.04em',
            paddingLeft: '3vw',
            fontFamily: "'Space Grotesk', sans-serif",
          }}
        >
          Surface Solutions of CT
        </span>
      </div>

      {/* ── Diagonal accent line ── */}
      <div
        className="absolute hidden lg:block pointer-events-none"
        style={{
          right: '28vw',
          top: 0,
          bottom: 0,
          width: '1px',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(201,168,76,0.10) 35%, rgba(201,168,76,0.10) 65%, transparent 100%)',
          transform: 'skewX(-6deg)',
        }}
      />

      {/* ── Main content ── */}
      <div
        className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-20 flex flex-col justify-center"
        style={{ minHeight: '100svh', paddingTop: '120px', paddingBottom: '110px' }}
      >
        <div className="max-w-[860px]">

          {/* Authorized pill */}
          <div className="animate-hero-1 inline-flex items-center gap-3 mb-10 px-5 py-2.5 border border-[#c9a84c]/25 bg-[#c9a84c]/5 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#c9a84c] animate-pulse-gold" />
            <span className="text-[#c9a84c] text-[10px] tracking-[0.36em] font-black">AUTHORIZED RESELLER — NANO PRO CERAMIC®</span>
          </div>

          {/* Headline */}
          <div className="mb-8 overflow-hidden">
            <h1 className="font-black leading-[0.88]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <span
                className="block animate-hero-3 whitespace-nowrap"
                style={{
                  fontSize: 'clamp(32px, 7vw, 100px)',
                  letterSpacing: '-0.045em',
                  WebkitTextStroke: '1.5px rgba(255,255,255,0.38)',
                  color: 'transparent',
                  lineHeight: '0.92',
                }}
              >
                Surface Protected
              </span>
              <span
                className="block animate-hero-3 text-white whitespace-nowrap"
                style={{ fontSize: 'clamp(32px, 7vw, 100px)', letterSpacing: '-0.045em', lineHeight: '0.92' }}
              >
                Surface Perfected
              </span>
            </h1>
          </div>

          {/* Subheadline */}
          <div className="animate-hero-4 flex items-center gap-5 mb-6">
            <div className="h-px w-10 bg-[#c9a84c]/50" />
            <span className="text-white/55 text-[10px] tracking-[0.2em] uppercase">Authorized Nano Pro Ceramic Reseller</span>
          </div>

          <p className="animate-hero-4 text-white/70 leading-[1.9] mb-12 max-w-[540px]" style={{ fontSize: 'clamp(13px, 1.1vw, 15px)' }}>
            Engineered surface protection for automotive, marine, aviation &amp; specialty applications —
            available through Surface Solutions of CT with expert guidance on the right system.
          </p>

          {/* CTAs */}
          <div className="animate-hero-5 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }}
              className="group inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#c9a84c] text-[#030303] text-[11px] font-black tracking-[0.22em] hover:bg-white transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              REQUEST PRODUCT INFO
              <i className="ri-arrow-right-line text-[14px] group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#featured"
              onClick={(e) => { e.preventDefault(); scrollTo('#featured'); }}
              className="inline-flex items-center justify-center gap-3 px-10 py-4 border border-white/16 text-white/58 text-[11px] font-semibold tracking-[0.18em] hover:border-white/35 hover:text-white hover:bg-white/4 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              EXPLORE COATINGS
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="animate-hero-5 absolute bottom-[116px] right-6 lg:right-14 hidden lg:flex flex-col items-center gap-2 opacity-25 pointer-events-none">
          <span className="text-white text-[9px] tracking-[0.3em] font-bold" style={{ writingMode: 'vertical-rl' }}>SCROLL</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent" />
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 bg-[#030303]/88 backdrop-blur-md" style={{ zIndex: 10 }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-20">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/5">
            {stats.map((s) => (
              <div key={s.label} className="py-5 px-5 lg:px-10 flex flex-col gap-1">
                <span
                  className="text-[#c9a84c] font-black leading-none text-gold-gradient"
                  style={{ fontSize: 'clamp(19px, 1.9vw, 25px)', fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {s.value}
                </span>
                <span className="text-white/28 text-[9px] tracking-[0.16em] uppercase">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
